import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, Optional, Renderer2, Self } from '@angular/core';
import { ControlContainer, FormGroupDirective } from '@angular/forms';
import { Subscription } from 'rxjs';

import { SecurityService } from '../../../core/security/security.service';
import { profileIsViewOnly } from '../../../core/security/permission-profiles';

/**
 * S2N-3076: legacy-tree twin of `[s2ReadOnly]` (shared/standalone/shared-forms/directives/
 * read-only.directive.ts) for the `pcg*` forms declared. Same
 * behaviour, same class name on the host; see the standalone directive for the full rationale.
 *
 *     <form [formGroup]="formGroup" pcgReadOnly ...>
 *
 * In short: for the View Only profile it disables the form once the view is ready and keeps it
 * disabled if component code re-enables it; for every other profile it does nothing at all.
 * Unlike `SecurityService.setFormAccess` it never calls `enable()`, so adding it to a form
 * cannot change what the operational profiles or Witness see today. Phase 5 applies it; Phase 3
 * ships the mechanism only. Never on the own-account security forms or the medication-use
 * stepper (they carry the writes View Only IS allowed). Not an authorization boundary — the
 * server refuses the write.
 */
@Directive({
    selector: '[pcgReadOnly]',
    standalone: false
})
export class ReadOnlyDirective implements AfterViewInit, OnDestroy {

    /** Attribute present with no value (`''`) enables the directive; an explicit `false` disables it. */
    @Input('pcgReadOnly') active: boolean | '' = '';

    private statusSub?: Subscription;
    private reapplying = false;
    private destroyed = false;

    constructor(
        private sec: SecurityService
        , private el: ElementRef
        , private renderer: Renderer2
        , @Self() @Optional() private formGroupDirective: FormGroupDirective
        , @Self() @Optional() private controlContainer: ControlContainer
    ) { }

    private get form() { return this.formGroupDirective?.form ?? this.controlContainer?.control ?? null; }

    ngAfterViewInit() {
        if (
            this.active === false
            || !profileIsViewOnly(this.sec.getSecurity()?.permissionProfile)
        ) { return; }
        // Deferred one microtask: this tick's bindings are already checked, and components whose
        // `disabled` reads the control (mat-input, pcg-select) would trip
        // ExpressionChangedAfterItHasBeenChecked in dev mode if disabled synchronously here.
        Promise.resolve().then(() => {
            const form = this.form;
            if (
                this.destroyed
                || form == null
            ) { return; }
            this.renderer.addClass(this.el.nativeElement, 's2-read-only');
            this.applyDisable(form);
            this.statusSub = form.statusChanges.subscribe(status => {
                if (
                    status !== 'DISABLED'
                    && !this.reapplying
                ) { this.applyDisable(form); }
            });
        });
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.statusSub?.unsubscribe();
    }

    private applyDisable(form: { disable: (opts?: { emitEvent?: boolean }) => void }) {
        this.reapplying = true;
        try { form.disable({ emitEvent: false }); }
        finally { this.reapplying = false; }
    }
}
